import axios from 'axios';

// Session AH — web push, client side. Shared by the Account toggle and the
// first-launch prompt so the two can never disagree about what "on" means.
//
// ⚠️ iOS reality, and it shapes everything below:
//   1. Push only reaches PWAs INSTALLED to the Home Screen. A Safari tab gets
//      nothing at all — no error, no prompt, no notification.
//   2. Notification.requestPermission() must be called from a real user
//      gesture. Calling it on page load is treated as a denial, and iOS makes
//      a denied permission very hard to recover: the person has to find it in
//      Settings. Never auto-request.

// True when running as an installed PWA rather than a browser tab.
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// 'unsupported' | 'needs-install' | 'denied' | 'off' | 'on'
export async function getPushState() {
  // ⚠️ The iOS check comes FIRST, before pushSupported(). Safari exposes
  // PushManager ONLY inside an installed web app — in a normal tab the feature
  // detection fails, so checking support first made 'needs-install'
  // unreachable on the exact platform it exists for, and iPhone users in a tab
  // were told their browser doesn't support notifications when the real answer
  // is "add it to your Home Screen". Found in testing, Session AH.
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (isIOS && !isStandalone()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

// The VAPID public key arrives base64url from the server; PushManager wants a
// Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Re-register an EXISTING browser subscription against whoever is signed in
// now. Called on every staff app load.
//
// ⚠️ This is what makes a shared device work, and its absence was a real bug
// found in testing. The push subscription belongs to the BROWSER INSTALLATION,
// not to a person. When a second staff member signed in on the same phone, the
// browser still held the first person's subscription — so the Account page read
// it and displayed "Notifications on" while the database row still pointed at
// the first user. The second person was told they were subscribed and received
// nothing; the first kept receiving alerts on a phone they had handed over.
//
// The server's ON CONFLICT (endpoint) DO UPDATE SET user_id was already written
// to handle exactly this, but nothing ever re-sent an existing subscription
// after a login, so the conflict never occurred. This is the road to it.
//
// Deliberately silent: it is a background repair, not a user action, and must
// never surface an error or ask for permission.
export async function syncPushSubscription() {
  try {
    if (!pushSupported()) return;              // guards serviceWorker.ready,
    if (Notification.permission !== 'granted') return;  // which never resolves
    const reg = await navigator.serviceWorker.ready;    // without a registration
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await axios.post('/api/push/subscribe', { subscription: sub.toJSON() });
  } catch {
    /* background repair — never surfaces */
  }
}

// ⚠️ MUST be called from a click handler. Returns { ok, reason }.
export async function subscribeToPush() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };

  const { data } = await axios.get('/api/push/public-key');
  // The server decides whether push is available on this environment. If the
  // VAPID keys are unset there is nothing to subscribe to, and saying so is
  // better than a subscription that silently never delivers.
  if (!data.enabled || !data.key) return { ok: false, reason: 'server-disabled' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission };

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }

  // Sent on every enable, not only on first creation. The endpoint is UNIQUE
  // server-side and the insert re-points it at the current user — which is what
  // makes a shared phone behave: whoever is signed in now gets the alerts.
  await axios.post('/api/push/subscribe', { subscription: sub.toJSON() });
  return { ok: true };
}

export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    // Server first: if the browser unsubscribes and the POST then fails, the
    // row is stranded and the person keeps getting notifications they turned
    // off. This order leaves at worst a dead row, which sendPush deletes on the
    // next 404/410.
    await axios.post('/api/push/unsubscribe', { endpoint: sub.endpoint });
    await sub.unsubscribe();
    return { ok: true };
  } catch {
    return { ok: false };
  }
}
